/**
 * VLX Combobox
 * Searchable select with client + remote support
 */

(function () {
    "use strict";

    const VLX = (window.VLX = window.VLX || {});
    let nextId = 0;

    class Combobox {
        constructor(select) {
            this.select = select;
            this.config = this.parseConfig();
            this.multiple = select.multiple;

            this.activeIndex = -1;
            this.results = [];
            this.timer = null;

            this.build();
            this.bind();

            this.dispatch("init");
        }

        parseConfig() {
            return {
                source: this.select.dataset.source || "client",
                url: this.select.dataset.url || null,
                minChars: parseInt(this.select.dataset.minChars || 0),
                placeholder: this.select.dataset.placeholder || "",
                emptyText: this.select.dataset.emptyText ?? "No results",
                loadingText: this.select.dataset.loadingText ?? "Loading...",
                removeText: this.select.dataset.removeText ?? "Remove {label}",
                debounce: parseInt(this.select.dataset.debounce || 250),
            };
        }

        build() {
            // Wrapper
            this.wrapper = document.createElement("div");
            this.wrapper.classList.add("combobox");
            this.wrapper.classList.toggle("combobox--multiple", this.multiple);

            // Control groups input and selected values for multiple selects
            this.control = document.createElement("div");
            this.control.classList.add("combobox__control");

            this.selections = document.createElement("div");
            this.selections.classList.add("combobox__selections");

            // Input
            this.input = document.createElement("input");
            this.input.type = "text";
            this.input.disabled = this.select.disabled;
            this.input.autocomplete = "off";
            this.input.setAttribute("role", "combobox");
            this.input.setAttribute("aria-autocomplete", "list");
            this.input.setAttribute("aria-expanded", "false");
            const firstOption = this.select.options[0];

            const hasEmptyOption = firstOption && firstOption.value === "";

            if (this.config.placeholder) {
                // expliziter Placeholder gewinnt
                this.input.placeholder = this.config.placeholder;
            } else if (hasEmptyOption) {
                // fallback: erste Option als Placeholder nutzen
                this.input.placeholder = firstOption.text;

                // optional: aus Suchdaten entfernen
                this.options = Array.from(this.select.options).slice(1);
            } else {
                this.input.placeholder = "";
            }

            // Dropdown
            this.dropdown = document.createElement("div");
            this.dropdown.classList.add("combobox__dropdown", "dropdown__menu");
            this.dropdown.id = `${this.select.id || `vlx-combobox-${++nextId}`}-listbox`;
            this.dropdown.setAttribute("role", "listbox");
            this.dropdown.setAttribute("aria-multiselectable", String(this.multiple));
            this.input.setAttribute("aria-controls", this.dropdown.id);

            // Select verstecken
            this.select.style.display = "none";

            // DOM
            this.select.parentNode.insertBefore(this.wrapper, this.select);
            this.wrapper.appendChild(this.control);
            if (this.multiple) {
                this.control.appendChild(this.selections);
            }
            this.control.appendChild(this.input);
            this.wrapper.appendChild(this.dropdown);
            this.wrapper.appendChild(this.select);

            // Initialwert
            const selected = this.select.selectedOptions[0];
            if (!this.multiple && selected && selected.value) {
                this.input.value = selected.text;
            }

            if (this.multiple) {
                this.renderSelections();
            }

            // Client Optionen
            if (this.config.source === "client") {
                this.options = Array.from(this.select.options);

                this.emptyOption = this.options.find((opt) => opt.value === "");

                this.options = this.options.filter((opt) => opt.value !== "");
            }
        }

        bind() {
            this.input.addEventListener("input", () => this.onInput());
            this.input.addEventListener("keydown", (e) => this.onKeydown(e));
            this.input.addEventListener("focus", () => this.onFocus());

            this.control.addEventListener("click", () => {
                if (!this.input.disabled) this.input.focus();
            });

            document.addEventListener("click", (e) => {
                const path = e.composedPath ? e.composedPath() : [];
                const clickedInside = path.includes(this.wrapper) || this.wrapper.contains(e.target);

                if (!clickedInside) {
                    this.close();
                }
            });
        }

        onFocus() {
            if (this.config.source === "client") {
                this.results = this.options;
                this.render();
            }
        }

        onInput() {
            const term = this.input.value.trim();

            if (this.config.source === "client") {
                this.results = this.options.filter((opt) => opt.text.toLowerCase().includes(term.toLowerCase()));
                this.render();
            }

            if (this.config.source === "remote") {
                if (term.length < this.config.minChars) {
                    this.close();
                    return;
                }

                clearTimeout(this.timer);

                this.timer = setTimeout(() => {
                    this.fetch(term);
                }, this.config.debounce);
            }
        }

        async fetch(term) {
            if (!this.dispatch("search")) return;

            this.setLoading(true);

            try {
                const res = await fetch(`${this.config.url}?q=${encodeURIComponent(term)}`);
                const data = await res.json();

                this.results = data;
                this.render();

                this.dispatch("loaded");
            } catch (e) {
                console.error(e);
                this.dispatch("error");
            } finally {
                this.setLoading(false);
            }
        }

        onKeydown(e) {
            if (e.key === "Backspace" && this.multiple && !this.input.value) {
                const selected = Array.from(this.select.selectedOptions);
                const last = selected[selected.length - 1];
                if (last) {
                    e.preventDefault();
                    this.removeValue(last.value);
                }
                return;
            }

            if (!this.results.length) return;

            switch (e.key) {
                case "ArrowDown":
                    e.preventDefault();
                    this.activeIndex = Math.min(this.activeIndex + 1, this.results.length - 1);
                    this.highlight();
                    this.scrollIntoView();
                    break;

                case "ArrowUp":
                    e.preventDefault();
                    this.activeIndex = Math.max(this.activeIndex - 1, 0);
                    this.highlight();
                    this.scrollIntoView();
                    break;

                case "Enter":
                    e.preventDefault();
                    if (this.activeIndex >= 0) {
                        this.selectItem(this.results[this.activeIndex]);
                    }
                    break;

                case "Escape":
                    this.close();
                    break;
            }
        }

        render() {
            this.dropdown.innerHTML = "";
            this.activeIndex = -1;

            if (!this.results.length) {
                this.renderMessage("combobox__empty", this.config.emptyText);
                this.open();
                return;
            }

            this.results.forEach((item, index) => {
                const el = document.createElement("div");
                el.classList.add("combobox__item", "dropdown__item");

                const label = item.label || item.text;
                el.textContent = label;
                el.setAttribute("role", "option");

                const selected = this.isSelected(item.value);
                el.classList.toggle("is-selected", selected);
                el.setAttribute("aria-selected", String(selected));

                el.addEventListener("click", () => this.selectItem(item));

                this.dropdown.appendChild(el);
            });

            this.open();
        }

        highlight() {
            const items = this.dropdown.querySelectorAll(".combobox__item");

            items.forEach((el, i) => {
                el.classList.toggle("is-active", i === this.activeIndex);
            });
        }

        scrollIntoView() {
            const items = this.dropdown.querySelectorAll(".combobox__item");
            const active = items[this.activeIndex];
            if (active) {
                active.scrollIntoView({ block: "nearest" });
            }
        }

        selectItem(item) {
            const value = item.value;
            const label = item.label || item.text;
            const option = this.ensureOption(value, label);

            if (this.multiple) {
                option.selected = !option.selected;
                this.input.value = "";
                this.renderSelections();
                this.render();
                this.input.focus();

                this.dispatch("select", {
                    value,
                    values: this.values(),
                    selected: option.selected,
                });
                this.select.dispatchEvent(new Event("change", { bubbles: true }));
                return;
            }

            this.select.value = value;
            this.input.value = label;

            this.dispatch("select", { value });
            this.select.dispatchEvent(new Event("change", { bubbles: true }));

            this.close();
        }

        ensureOption(value, label) {
            let option = Array.from(this.select.options).find((item) => item.value === String(value));

            if (!option) {
                option = new Option(label, value);
                this.select.add(option);
            }

            return option;
        }

        isSelected(value) {
            return Array.from(this.select.selectedOptions).some((option) => option.value === String(value));
        }

        values() {
            return Array.from(this.select.selectedOptions).map((option) => option.value);
        }

        removeValue(value) {
            const option = Array.from(this.select.options).find((item) => item.value === String(value));
            if (!option) return;

            const wasOpen = this.wrapper.classList.contains("is-open");
            option.selected = false;
            this.renderSelections();
            if (wasOpen && this.results.length) this.render();

            this.dispatch("select", {
                value,
                values: this.values(),
                selected: false,
            });
            this.select.dispatchEvent(new Event("change", { bubbles: true }));
        }

        renderSelections() {
            if (!this.multiple) return;

            this.selections.innerHTML = "";

            Array.from(this.select.selectedOptions).forEach((option) => {
                if (!option.value) return;

                const tag = document.createElement("span");
                tag.classList.add("combobox__tag");
                tag.textContent = option.text;

                const remove = document.createElement("button");
                remove.type = "button";
                remove.classList.add("combobox__tag-remove");
                remove.setAttribute("aria-label", this.config.removeText.replaceAll("{label}", () => option.text));
                remove.textContent = "×";
                remove.disabled = this.select.disabled;
                remove.addEventListener("click", (event) => {
                    event.stopPropagation();
                    this.removeValue(option.value);
                    this.input.focus();
                });

                tag.appendChild(remove);
                this.selections.appendChild(tag);
            });
        }

        renderMessage(className, text) {
            const message = document.createElement("div");
            message.classList.add(className);
            message.textContent = text;
            this.dropdown.replaceChildren(message);
        }

        setLoading(state) {
            this.wrapper.classList.toggle("is-loading", state);

            if (state) {
                this.renderMessage("combobox__loading", this.config.loadingText);
                this.open();
            }
        }

        open() {
            this.dropdown.style.display = "block";
            this.wrapper.classList.add("open", "is-open");
            this.input.setAttribute("aria-expanded", "true");
        }

        close() {
            this.dropdown.style.display = "none";
            this.wrapper.classList.remove("open", "is-open");
            this.input.setAttribute("aria-expanded", "false");
            this.activeIndex = -1;
        }

        dispatch(name, detail = {}) {
            const event = new CustomEvent(`vlx.combobox.${name}`, {
                cancelable: true,
                detail: Object.assign({ element: this.select }, detail),
            });

            return this.select.dispatchEvent(event);
        }
    }

    // Data API
    document.addEventListener("DOMContentLoaded", function () {
        document.querySelectorAll("select[data-combobox]").forEach((el) => {
            if (!el.__vlxCombobox) {
                el.__vlxCombobox = new Combobox(el);
            }
        });
    });

    VLX.Combobox = Combobox;
})();
